import React, { Children, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import Button from 'common/Button';
import Spinner from 'common/Spinner';
import RawHtml from 'common/RawHtml';
import isMobileDevice from 'utils/isMobileDevice';
import backButtonStyles from 'groups/styles/backButton';
import BottomContent from 'common/BottomContent';
import ErrorMsg from 'common/ErrorMsg';
import hasError from 'utils/hasError';
import pick from 'lodash/pick';
import groupActions from 'core/groups/actions';

import uiActions from 'core/ui/actions';
import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    back: backButtonStyles(theme),
    button: {
      margin: [0, 0, theme.spacing.xs, 0],
    },
    buttonRow: {
      display: 'flex',
      flexDirection: 'column',
    },
    fundingOption: {
      '& button': {
        '&:hover': {
          background: 'inherit',
        },
      },
      margin: [0, 0, theme.spacing.lg, 0],
    },
    hideButtonRow: {
      display: 'none',
    },
    isOpened: {},
    root: {
      '& .field-type--amount-selector': {
        margin: [0, 0, theme.spacing.lg, 0],
      },
      '& .field-type--amount-selector .flyup': {
        '& small': {
          fontWeight: 'normal',
        },
        fontSize: 14,
        fontWeight: theme.typography.fontWeights.bold,
        textTransform: 'none',
        top: -15,
      },
      '& .field-type--amount-selector .flyup:not(.hasError)': {
        color: theme.palette.text,
      },
      '& .field-type--lookup': {
        marginBottom: theme.spacing.md,
      },
    },
    tabpanel: {
      '&$isOpened': {
        display: 'block',
      },
      display: 'none',
    },
    toggleContent: {
      ...theme.typography.p,
      ...theme.typography.p1,
      padding: [theme.spacing.sm, 0, theme.spacing.xl, 0],
      textAlign: 'center',
      [`@media(min-width: ${theme.screenSizes.mobileMax}px)`]: {
        fontSize: theme.typography.p1.desktopFontSize,
      },
    },
  };
};

const FundingAmountBare = ({
  backAllowed,
  backButtonLabel,
  bottomContent,
  children,
  classes,
  disableButton,
  fundingOptionText,
  hasFields,
  isFundNow,
  isMultipleFundingOptions,
  label,
  labelMobile,
  loading,
  name: groupName,
  onBack,
  onContinueClick,
  onSave,
  saveAndContinueAllowed,
  saveButtonLabel,
  subText,
  submitButtonLabel,
}) => {
  const dispatch = useDispatch();
  const oauthStateid = useSelector(({ ui }) => ui.oauth_state_id);
  const name = groupName.toLowerCase();
  const { fundingOption, restChildren } = Children.toArray(children?.props?.children)
    ?.filter((child, i, ary) => {
      /* If only 1 product, hide total amount field */
      if (child.props?.fieldName === 'appRequest_totalFundingAmount') {
        const numProducts = ary.filter((c) => c.props?.fieldName.includes('_amount___')).length;
        return numProducts > 1;
      }
      return true;
    })
    ?.reduce(
      (obj, child) => {
        if (child.props?.fieldName === 'appRequest_fundingOption') {
          return {
            ...obj,
            fundingOption: [child],
          };
        }
        return {
          ...obj,
          restChildren: [...obj.restChildren, child],
        };
      },
      { fundingOption: [], restChildren: [] },
    );
  useEffect(() => {
    const plaidLinkToken = localStorage.getItem('plaidLinkToken');
    if (oauthStateid && plaidLinkToken) {
      dispatch(uiActions.setActiveGroup('FUNDING_TRANSFER_EXT'));
    }
  });
  return (
    <>
      <legend
        className={classnames('grp-label', classes.label)}
        role={!hasFields ? 'presentation' : undefined}
      >
        <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
          {label}
        </RawHtml>
        <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
          {labelMobile}
        </RawHtml>
      </legend>
      <div className={classnames('region-funding-amount-group', classes.root)}>
        <input type="hidden" value="break-autocomplete" />
        <RawHtml className={classnames('content center', classes.content)}>{subText}</RawHtml>

        <ErrorMsg name={groupName} type="group" />

        <div className={classnames('region-funding-option', classes.fundingOption)}>
          {fundingOption}
        </div>
        <div
          className={classnames('ReactCollapse--collapse', classes.tabpanel, {
            [classes.isOpened]: isFundNow,
            displayNone: !isFundNow,
          })}
          role={isMultipleFundingOptions ? 'tabpanel' : undefined}
          id="panel0"
          aria-labelledby={isMultipleFundingOptions ? 'tab0' : undefined}
        >
          <div className={classnames('region-fields', classes.fields)}>{restChildren}</div>
        </div>
        <div
          className={classnames('ReactCollapse--collapse', classes.tabpanel, {
            [classes.isOpened]: !isFundNow,
            displayNone: isFundNow,
          })}
          role={isMultipleFundingOptions ? 'tabpanel' : undefined}
          id="panel1"
          aria-labelledby={isMultipleFundingOptions ? 'tab1' : undefined}
        >
          <RawHtml className={classnames('toggle-content', classes.toggleContent)}>
            {fundingOptionText}
          </RawHtml>
        </div>

        <BottomContent className="bottomContent">{bottomContent}</BottomContent>

        <div
          className={classnames('row btn-row-group', `btn-group-${name}`, classes.buttonRow, {
            [classes.hideButtonRow]: isFundNow || fundingOption.length === 0,
          })}
        >
          {(!isMobileDevice() || !isFundNow || fundingOption.length === 0) && (
            <Button
              className={classnames(
                'btn btn-mobile btn-grp-next',
                `btn-next-${name}`,
                'mt-5',
                {
                  loading,
                },
                classes.button,
              )}
              style={{ display: !isFundNow ? 'inline-block' : '' }}
              data-name="btn-grp-next"
              onClick={onContinueClick}
              disabled={disableButton}
            >
              <RawHtml type="span">{submitButtonLabel}</RawHtml>{' '}
              <i className="icon-arrow-thin-right" aria-hidden="true" />
              <Spinner loading={loading} />
            </Button>
          )}
          {saveAndContinueAllowed && (
            <RawHtml
              className={classnames(
                'btn btn-mobile btn-tertiary btn-grp-save-and-continue',
                classes.button,
              )}
              data-name="btn-grp-save-and-continue"
              type="button"
              disabled={disableButton && isFundNow}
              onClick={onSave}
              variant="secondary"
            >
              {saveButtonLabel}
            </RawHtml>
          )}

          {backAllowed && (
            <RawHtml
              className={classnames(
                'btn btn-mobile btn-tertiary btn-grp-back mt-5',
                classes.back,
                classes.button,
              )}
              data-name="btn-grp-back"
              type="button"
              onClick={onBack}
              variant="secondary"
            >
              {backButtonLabel}
            </RawHtml>
          )}
        </div>
      </div>
    </>
  );
};

FundingAmountBare.propTypes = {
  backAllowed: PropTypes.bool.isRequired,
  backButtonLabel: PropTypes.string,
  bottomContent: PropTypes.string,
  children: PropTypes.node.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  disableButton: PropTypes.bool.isRequired,
  fundingOptionText: PropTypes.string,
  hasFields: PropTypes.bool,
  isFundNow: PropTypes.bool.isRequired,
  isMultipleFundingOptions: PropTypes.bool.isRequired,
  label: PropTypes.string,
  labelMobile: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onContinueClick: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveAndContinueAllowed: PropTypes.bool,
  saveButtonLabel: PropTypes.string,
  subText: PropTypes.string,
  submitButtonLabel: PropTypes.string,
};

FundingAmountBare.defaultProps = {
  backButtonLabel: '',
  bottomContent: '',
  fundingOptionText: null,
  hasFields: false,
  label: '',
  labelMobile: '',
  saveAndContinueAllowed: false,
  saveButtonLabel: '',
  subText: '',
  submitButtonLabel: '',
};

const mapStateToProps = ({ data, fields, groups, page, resources, ui }, { name }) => {
  const group = groups[name];
  const loading = !!(ui.groupLoading || ui.fieldLoading || ui.appLoading || ui.pageLoading);
  return {
    backAllowed: page.backAllowed || Object.keys(groups)[0] !== name,
    backButtonLabel: group.backButtonLabel,
    bottomContent: group.bottomContent,
    disableButton: loading || hasError(pick(fields, group.fields)) || ui.menuOpen || false,
    fundingOptionText: resources.fundingOption_text,
    hasFields: group?.fields?.length > 0 ?? false,
    isFundNow: data.appRequest_fundingOption === 'Fund Now' || !data.appRequest_fundingOption,
    isMultipleFundingOptions: fields.appRequest_fundingOption?.selectorValues?.length > 1 ?? false,
    label: group.label,
    labelMobile: group.labelMobile,
    loading,
    saveAndContinueAllowed: page.saveAndContinueAllowed,
    saveButtonLabel: page.saveButtonLabel,
    subText: group.subText,
    submitButtonLabel: group.submitButtonLabel,
  };
};

const mapDispatchToProps = (dispatch, { name }) => ({
  onBack: () => {
    dispatch(groupActions.back());
  },
  onContinueClick: () => {
    dispatch(groupActions.next(name));
  },
  onSave: () => {
    dispatch(groupActions.save(name));
  },
});

const FundingAmountStyled = withStyles(styles)(FundingAmountBare);

export default connect(mapStateToProps, mapDispatchToProps)(FundingAmountStyled);
